/** Reports (S80), the map (S81) and the day-90 read (S82). docs/44 §1, docs/50 §4.8, docs/52 §3.10. */
import { z } from 'zod';
import { AppPath, Day, HttpsUrl, IsoDateTime, Provenance, Slug } from '../common.ts';
import { MapMovement, ReportKey, ReportPeriod } from '../report-period.ts';
import { GridPurpose, GuaranteeResult, ReportStatus } from '../enums.ts';
import { GridRunId, ReportId } from '../ids.ts';
import { Day90Outcome } from '../scans.ts';
import { LedgerItem, LedgerKind } from './shared.ts';

/** One map run's points, served by `gridPoints`: defined in grid-points.ts (a leaf module, so recon.ts can use it). */
import { GridPoints } from '../grid-points.ts';
export { GridPoints };

/**
 * `month` is the report's key (c136): the scan day ("2026-10-03") for scan-keyed reports, a month for older ones.
 * `period` says what the report covers ("Sep 5 to Oct 3, 2026"); show it wherever the report is named.
 */
export const ReportSummary = z.object({
  id: ReportId,
  month: ReportKey,
  status: ReportStatus,
  headline: z.string().max(200).nullable(),
  publishedAt: IsoDateTime.nullable(),
  period: ReportPeriod.nullable().optional(),
});

export const ReportList = z.object({
  items: z.array(ReportSummary),
  /** "Your first report lands on Oct 30." (docs/52 §5 empty state) */
  nextReportOn: Day.nullable(),
});

const Metric = z.object({ label: z.string().max(80), value: z.number(), lastMonth: z.number().nullable(), note: z.string().max(200).nullable() }).extend(Provenance.shape);

/**
 * One number in the monthly proof report (r26, measurement spec §4.1): this cycle, last cycle and the change.
 * `thisCycle` is null when it wasn't measured, with the reason in `notMeasured`: a gap is never shown as zero.
 * `change.percent` is null when last cycle was under 10, so the screen shows the count (2 to 4 isn't "+100%").
 */
export const ReportNumberKey = z.enum([
  'enquiries',
  'site_forms',
  'site_call_taps',
  'gbp_call_taps',
  'site_visits',
  'site_email_taps',
  'site_direction_taps',
  'quotes_started',
  'bookings_opened',
  'gbp_direction_requests',
  'gbp_website_clicks',
  'reviews_new',
]);
export type ReportNumberKey = z.infer<typeof ReportNumberKey>;

export const ReportNumber = z
  .object({
    key: ReportNumberKey,
    label: z.string().max(80),
    group: z.enum(['headline', 'website', 'google', 'reviews']),
    thisCycle: z.number().int().min(0).nullable(),
    lastCycle: z.number().int().min(0).nullable(),
    change: z.object({ delta: z.number().int(), percent: z.number().int().nullable() }).nullable(),
    /** Why there's no number this cycle ("Google hasn't given us access to your profile numbers yet."). */
    notMeasured: z.string().max(200).nullable(),
    /** Whether it's one of the parts the enquiries headline adds up. */
    inHeadline: z.boolean(),
    /** What the number is and isn't, in one line. */
    note: z.string().max(200).nullable(),
  })
  .extend(Provenance.shape);
export type ReportNumber = z.infer<typeof ReportNumber>;

const Points = z.number().min(0).max(81);
const ReportMapBase = z.object({
  serviceSlug: Slug,
  keyword: z.string().max(120),
  /** Top-three points of 81 on this report's scan (the average of the pair on a day 90 report). */
  top3Points: Points,
  top3Share: z.number().min(0).max(1),
  /** Day one: the average of the two baseline scans. */
  baselinePoints: Points.nullable(),
  /** The scan before this one. */
  lastPoints: Points.nullable(),
  /** Against day one, with the held band (c137). */
  movement: MapMovement.nullable(),
});
/** As jobs stores it: the runs by id. */
export const ReportMapStored = ReportMapBase.extend({ gridRunId: GridRunId, baselineGridRunId: GridRunId.nullable() });
/** As the app shows it: the runs as points URLs for @ff/rank-map. */
export const ReportMapView = ReportMapBase.extend({ pointsUrl: AppPath, baselinePointsUrl: AppPath.nullable() });

export const ReportOverall = z.object({ top3Points: Points, baselinePoints: Points.nullable(), lastPoints: Points.nullable(), movement: MapMovement.nullable() });

/** "What we did" by kind, counts first (docs/44 §1.2 block 5). */
export const LedgerCount = z.object({ kind: LedgerKind, label: z.string().max(80), count: z.number().int().min(1) });

/** Blocks follow docs/44 §1.2 in order. Every block carries its "i" text. */
export const Report = z.object({
  id: ReportId,
  month: ReportKey,
  status: ReportStatus,
  /** From the scan before (or go-live) to this report's scan (c136). */
  period: ReportPeriod.nullable().optional(),
  /** Up, held or down against the baseline pair, "held" meaning within `heldBand` (c137). Null before a baseline. */
  mapMovement: MapMovement.nullable().optional(),
  headline: z.string().max(200),
  map: z
    .object({ serviceSlug: Slug, keyword: z.string().max(120), top3Points: z.number().int().min(0).max(81), dayOnePoints: z.number().int().min(0).max(81).nullable(), pointsUrl: AppPath })
    .nullable(),
  callsAndClicks: z.array(Metric),
  reviews: z.object({ newThisMonth: z.number().int().min(0), averageRating: z.number().min(0).max(5).nullable(), paceNote: z.string().max(200).nullable() }),
  whatWeDid: z.array(LedgerItem),
  yourSide: z.array(z.string().max(200)),
  whatsNext: z.array(z.string().max(300)),
  notes: z.array(z.string().max(300)),
  info: z.record(z.string(), z.string().max(400)),
  pdfUrl: AppPath.nullable(),
  publishedAt: IsoDateTime.nullable(),
  // ---- the monthly proof report (r26), added with the report engine. Optional so older reports still parse.
  /** The scan this report reads (c136). */
  scanDay: Day.nullable().optional(),
  /** The headline number: site forms plus site call taps plus Google profile call taps, where each is measured. */
  enquiries: ReportNumber.nullable().optional(),
  /** The supporting numbers, in the order to show them. */
  numbers: z.array(ReportNumber).max(20).optional(),
  /** One map per money service, in the agreed order. */
  maps: z.array(ReportMapView).max(3).optional(),
  /** The three services averaged: the number the day 90 promise reads. */
  overall: ReportOverall.nullable().optional(),
  whatWeDidCounts: z.array(LedgerCount).max(20).optional(),
});
export type Report = z.infer<typeof Report>;

/**
 * What the report engine (apps/jobs) writes to `reports.data_json`. Everything is optional: a report drafted before
 * a field existed still parses, and anything malformed reads as absent, never as a number.
 */
export const StoredReport = z
  .object({
    period: ReportPeriod.nullable(),
    scanDay: Day.nullable(),
    mapMovement: MapMovement.nullable(),
    headline: z.string().max(200),
    map: z.object({ serviceSlug: Slug, keyword: z.string().max(120), top3Points: z.number().int().min(0).max(81), dayOnePoints: z.number().int().min(0).max(81).nullable(), gridRunId: GridRunId }).nullable(),
    callsAndClicks: z.array(Metric),
    reviews: z.object({ newThisMonth: z.number().int().min(0), averageRating: z.number().min(0).max(5).nullable(), paceNote: z.string().max(200).nullable() }),
    enquiries: ReportNumber.nullable(),
    numbers: z.array(ReportNumber).max(20),
    maps: z.array(ReportMapStored).max(3),
    overall: ReportOverall.nullable(),
    whatWeDid: z.array(LedgerItem).max(60),
    whatWeDidCounts: z.array(LedgerCount).max(20),
    yourSide: z.array(z.string().max(200)),
    whatsNext: z.array(z.string().max(300)),
    notes: z.array(z.string().max(300)),
    info: z.record(z.string(), z.string().max(400)),
  })
  .partial();
export type StoredReport = z.infer<typeof StoredReport>;

/** What staff write over the draft in the console: each list replaces the drafted one when present. */
export const ReportNarrative = z
  .object({ yourSide: z.array(z.string().trim().min(1).max(200)).max(8), whatsNext: z.array(z.string().trim().min(1).max(300)).max(5), notes: z.array(z.string().trim().min(1).max(300)).max(8) })
  .partial();
export type ReportNarrative = z.infer<typeof ReportNarrative>;

export interface ReportRowLike {
  id: string;
  month: string;
  status: z.infer<typeof ReportStatus>;
  headline: string | null;
  data_json: string;
  narrative_json: string | null;
  pdf_r2_key: string | null;
  published_at: string | null;
}

function parseOr<T>(schema: z.ZodType<T>, json: string | null, fallback: T): T {
  if (!json) return fallback;
  try {
    const p = schema.safeParse(JSON.parse(json));
    return p.success ? p.data : fallback;
  } catch {
    return fallback;
  }
}

export const pointsUrlOf = (tenantId: string, gridRunId: string) => `/api/v1/t/${tenantId}/grid-runs/${gridRunId}/points`;

/**
 * A stored report as the client sees it. One function for the app (which shows it) and the console (which previews
 * it before publishing), so the two can't differ. `whatWeDid` is passed in for reports drafted before the engine
 * stored the ledger with them.
 */
export function viewOfReport(r: ReportRowLike, tenantId: string, opts: { pdf?: boolean; whatWeDid?: z.infer<typeof LedgerItem>[] } = {}): Report {
  const data = parseOr<StoredReport>(StoredReport, r.data_json, {});
  const narrative = parseOr<ReportNarrative>(ReportNarrative, r.narrative_json, {});
  const period = data.period ?? null;
  return {
    id: r.id,
    month: r.month,
    status: r.status,
    period,
    mapMovement: data.mapMovement ?? null,
    headline: (r.headline ?? data.headline ?? (period ? `Your report for ${period.label}` : `Your ${r.month} report`)).slice(0, 200),
    map: data.map ? { serviceSlug: data.map.serviceSlug, keyword: data.map.keyword, top3Points: data.map.top3Points, dayOnePoints: data.map.dayOnePoints, pointsUrl: pointsUrlOf(tenantId, data.map.gridRunId) } : null,
    callsAndClicks: data.callsAndClicks ?? [],
    reviews: data.reviews ?? { newThisMonth: 0, averageRating: null, paceNote: null },
    whatWeDid: data.whatWeDid ?? opts.whatWeDid ?? [],
    yourSide: narrative.yourSide ?? data.yourSide ?? [],
    whatsNext: narrative.whatsNext ?? data.whatsNext ?? [],
    notes: narrative.notes ?? data.notes ?? [],
    info: data.info ?? {},
    pdfUrl: opts.pdf && r.pdf_r2_key ? `/api/v1/t/${tenantId}/reports/${r.month}/pdf` : null,
    publishedAt: r.published_at,
    scanDay: data.scanDay ?? null,
    enquiries: data.enquiries ?? null,
    numbers: data.numbers ?? [],
    maps: (data.maps ?? []).map(({ gridRunId, baselineGridRunId, ...m }) => ({ ...m, pointsUrl: pointsUrlOf(tenantId, gridRunId), baselinePointsUrl: baselineGridRunId ? pointsUrlOf(tenantId, baselineGridRunId) : null })),
    overall: data.overall ?? null,
    whatWeDidCounts: data.whatWeDidCounts ?? [],
  };
}

export const ReportParams = z.object({ month: ReportKey });

export const ReportShareCreated = z.object({ url: HttpsUrl, expiresAt: IsoDateTime });

export const MapSeries = z.object({
  serviceSlug: Slug,
  keyword: z.string().max(120),
  runs: z.array(
    z.object({
      id: GridRunId,
      runAt: IsoDateTime,
      purpose: GridPurpose,
      top3: z.number().int().min(0).max(81).nullable(),
      /** JSON grid for packages/rank-map, served from the app origin. */
      pointsUrl: AppPath.nullable(),
    }),
  ),
  ledger: z.array(LedgerItem),
});

/** One map reading: the day, what it scored and the run to draw. A baseline or day 90 reading averages a pair. */
export const MapSnapshot = z.object({
  day: Day,
  purpose: GridPurpose,
  top3Points: Points,
  top3Share: z.number().min(0).max(1),
  /** The scans the number averages: 2 for the baseline and day 90 pairs, 1 otherwise. */
  scans: z.number().int().min(1).max(2),
  gridRunId: GridRunId,
  pointsUrl: AppPath,
});

/** The baseline map, the latest map and the change, for each money service (S81). */
export const MapsOverview = z.object({
  /** The grid, recorded at baseline and never changed (c011). Null until the baseline has run. */
  grid: z.object({ size: z.literal(9), points: z.literal(81), spacingMiles: z.number().positive().max(10), recordedOn: Day }).nullable(),
  services: z
    .array(
      z.object({
        serviceSlug: Slug,
        keyword: z.string().max(120),
        baseline: MapSnapshot.nullable(),
        latest: MapSnapshot.nullable(),
        /** Latest against baseline, in points of 81, with the held band. Null until both exist. */
        change: z.object({ points: z.number().min(-81).max(81), movement: MapMovement.nullable() }).nullable(),
      }),
    )
    .max(3),
  overall: z.object({ baselinePoints: Points.nullable(), latestPoints: Points.nullable(), movement: MapMovement.nullable() }).nullable(),
  nextScanOn: Day.nullable(),
});
export type MapsOverview = z.infer<typeof MapsOverview>;

const Day90Service = z.object({ serviceSlug: Slug, keyword: z.string().max(120), baseline: Points, day90: Points });
const Day90Habit = z.object({ weeksKept: z.number().int().min(0).max(12), weeksNeeded: z.literal(10), monthsKept: z.number().int().min(0).max(3), monthsNeeded: z.literal(2), met: z.boolean() });

/** What jobs stores in `guarantee_reads.inputs_json` for the day 90 read. */
export const Day90Inputs = z.object({
  baselineAverage: Points,
  day90Average: Points,
  outcome: Day90Outcome,
  services: z.array(Day90Service).max(3),
  habit: Day90Habit,
  twoJobs: z.object({ baseline: z.number().int().min(0).nullable(), day90: z.number().int().min(0).nullable() }),
  explanation: z.array(z.string().max(300)).max(20),
  lateDays: z.number().int().min(0),
  baselineDay: Day,
  day90Day: Day,
  gridRunIds: z.array(GridRunId).max(12),
});
export type Day90Inputs = z.infer<typeof Day90Inputs>;

/** The working is shown, the refund button only when it qualifies (docs/52 S82). */
export const Day90Read = z.object({
  available: z.boolean(),
  availableOn: Day.nullable(),
  baselineAverage: z.number().min(0).max(81).nullable(),
  day90Average: z.number().min(0).max(81).nullable(),
  twoJobs: z.object({ baseline: z.number().int().min(0).nullable(), day90: z.number().int().min(0).nullable() }),
  result: GuaranteeResult.nullable(),
  explanation: z.array(z.string().max(300)),
  canClaimRefund: z.boolean(),
  computedAt: IsoDateTime.nullable(),
  // ---- added with the day 90 read (c009, c010, r01). Optional so a read stored before them still parses.
  /** Promise kept, missed with the two jobs done (month three back is a staff decision), or missed without them. */
  outcome: Day90Outcome.nullable().optional(),
  services: z.array(Day90Service).max(3).optional(),
  /** The client's two jobs as counted: weeks with a review ask of 12, months with photos of 3, no-jobs marks kept. */
  habit: Day90Habit.nullable().optional(),
  /** Days after day 90 the scans ran, when the service was paused on the day. */
  lateDays: z.number().int().min(0).optional(),
});
export type Day90Read = z.infer<typeof Day90Read>;
